import LocalizedTextField from '@components/Customization/LocalizedTextField';
import ToggleRow from '@components/Customization/ToggleRow';
import FieldSettings from '@components/Customization/PoiTypes/FieldSettings';
import RuleEditor, { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { FieldTypeIcon } from '@components/Customization/fieldTypes';
import { chosenIds, withDefaultToggled, withoutChoice, withType } from '@lib/customization/fieldDraft';
import { conditionComplete, isAlwaysAnswered } from '@lib/customization/poiRules';
import { useLocalization } from '@lib/localization';

import {
  Box, Button, Divider, Drawer, FormControl, FormHelperText, FormLabel, IconButton, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';
import {
  buttonStyles, drawerBodyStyles, drawerCloseStyles, drawerFooterStyles, drawerHeaderStyles, drawerPaperStyles,
  stepLabelStyles, stepNumberStyles, typeCardOptionSelectedStyles, typeCardOptionStyles, typeGridStyles,
} from '@pages/CustomPoiTypes.styles';
import {
  formInputTypes, hasText, newEntityId, ruleTargets, type FormInputType,
  type PoiField, type PoiFieldOption,
} from '@store/api/poiTypeEndpoints';
import { useEffect, useState } from 'react';
import { PlusIcon, StarIcon, StarOIcon, TimesIcon, TrashIcon } from 'react-line-awesome';

/** `'d'` is the pseudo-group for fields sitting directly on the type. */
export type GroupTarget = 'd' | number;

interface Props {
  open: boolean;
  /** The field being edited, or null when adding a new one. */
  field: PoiField | null;
  group: GroupTarget;
  /** Group names for the "which section" picker, in the type's own order. */
  groupNames: string[];
  /** Fields a rule may point at: this type's own and every ancestor's, already resolved. */
  candidates: RuleCandidate[];
  typeName: string;
  saving: boolean;
  onClose: () => void;
  onSave: (field: PoiField, group: GroupTarget) => void;
}

const emptyField = (): PoiField => ({
  label: {}, type: 'Text', visible: true, required: false, readOnly: false, showOnReports: true,
  multiline: false,
});

/**
 * Whether every condition is answerable as written. Caught here rather than at save: the
 * API would reject the same thing, but only after the drawer had closed on the work.
 */
const rulesAreComplete = (rules: PoiField['rules']): boolean =>
  (rules ?? []).every((rule) => rule.when.conditions.every(conditionComplete));

/** Numbered section header, mirroring the prototype's three-step drawer. */
const Step: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <Stack direction="row" sx={stepLabelStyles}>
    <Box component="span" sx={stepNumberStyles}>{n}</Box>
    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
      {children}
    </Typography>
  </Stack>
);

const FieldDrawer: React.FC<Props> = ({
  open, field, group, groupNames, candidates, typeName, saving, onClose, onSave,
}) => {
  const { t } = useLocalization();

  const [draft, setDraft] = useState<PoiField>(emptyField());
  const [target, setTarget] = useState<GroupTarget>('d');

  useEffect(() => {
    if (!open) return;
    setDraft(field ? { ...field } : emptyField());
    setTarget(group);
  }, [open, field, group]);

  const patch = (changes: Partial<PoiField>) => setDraft((current) => ({ ...current, ...changes }));

  const changeType = (type: FormInputType) => setDraft((current) => withType(current, type));

  const isChoice = draft.type === 'List' || draft.type === 'MultiList';
  const alwaysAnswered = isAlwaysAnswered(draft.type);
  const options = draft.options ?? [];
  const chosen = chosenIds(draft);

  const setOption = (index: number, option: PoiFieldOption) =>
    patch({ options: options.map((current, i) => (i === index ? option : current)) });

  const removeOption = (index: number) => patch(withoutChoice(draft, index));

  const toggleDefault = (option: PoiFieldOption) => option.id && patch(withDefaultToggled(draft, option.id));

  const canSave = hasText(draft.label) && !saving
    && (!isChoice || options.every((option) => hasText(option.label)))
    && rulesAreComplete(draft.rules);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { ...drawerPaperStyles, width: { xs: '100%', sm: 560 } } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={drawerHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>
              {field ? t('Customization_EditField') : t('Customization_NewField')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{typeName}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={drawerCloseStyles} disabled={saving}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ ...drawerBodyStyles, flex: 1, overflowY: 'auto' }}>
          <Step n={1}>{t('Customization_FieldType')}</Step>
          <Box sx={{ ...typeGridStyles, mb: 3.5 }}>
            {formInputTypes.map((type) => (
              <Stack
                key={type}
                sx={{ ...typeCardOptionStyles, ...(draft.type === type ? typeCardOptionSelectedStyles : {}) }}
                onClick={() => changeType(type)}
              >
                <FieldTypeIcon type={type} size={32} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.2 }}>
                  {t(`Customization_FieldType_${type}`)}
                </Typography>
              </Stack>
            ))}
          </Box>

          <Step n={2}>{t('Customization_Configure')}</Step>
          <Stack spacing={2.5}>
            <TextField
              select
              size="small"
              label={t('Customization_Group')}
              value={String(target)}
              onChange={(event) => setTarget(event.target.value === 'd' ? 'd' : Number(event.target.value))}
              helperText={t('Customization_GroupHelp')}
            >
              <MenuItem value="d">{t('Customization_NoGroup')}</MenuItem>
              {groupNames.map((name, index) => (
                <MenuItem key={index} value={String(index)}>{name}</MenuItem>
              ))}
            </TextField>

            <LocalizedTextField
              required
              autoFocus
              label={t('Customization_Label')}
              value={draft.label}
              onChange={(label) => patch({ label })}
              helperText={t('Customization_LabelHelp')}
            />

            <TextField
              size="small"
              label={t('Customization_HelpText')}
              value={draft.help ?? ''}
              onChange={(event) => patch({ help: event.target.value })}
            />

            <FieldSettings
              draft={draft}
              patch={patch}
              choices={isChoice && (
                <FormControl fullWidth>
                  <FormLabel sx={{ typography: 'caption', color: 'text.secondary', mb: 1 }}>
                    {t('Customization_Choices')}
                  </FormLabel>

                  <Stack spacing={1.5}>
                    {/* Keyed by the option's own id, not its position: with an index key React
                        reuses a removed row's state for whatever slides up into its place. */}
                    {options.map((option, index) => {
                      const isDefault = Boolean(option.id && chosen.includes(option.id));

                      return (
                        <Stack key={option.id ?? index} direction="row" spacing={1} alignItems="center">
                          <IconButton
                            size="small"
                            color={isDefault ? 'warning' : 'default'}
                            title={isDefault ? t('Customization_ClearDefault') : t('Customization_MarkDefault')}
                            onClick={() => toggleDefault(option)}
                          >
                            {isDefault ? <StarIcon /> : <StarOIcon />}
                          </IconButton>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <LocalizedTextField
                              label={`${t('Customization_Choice')} ${index + 1}`}
                              value={option.label}
                              onChange={(label) => setOption(index, { ...option, label })}
                            />
                          </Box>

                          <IconButton
                            size="small"
                            disabled={options.length <= 1}
                            title={options.length <= 1 ? t('Customization_LastChoice') : t('Common_Delete')}
                            onClick={() => removeOption(index)}
                          >
                            <TrashIcon />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  </Stack>

                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      startIcon={<PlusIcon />}
                      sx={buttonStyles}
                      onClick={() => patch({ options: [...options, { id: newEntityId(), label: {} }] })}
                    >
                      {t('Customization_AddChoice')}
                    </Button>
                  </Box>

                  <FormHelperText sx={{ mx: 0, mt: 1 }}>
                    {draft.type === 'List' ? t('Customization_StarSingleDefault') : t('Customization_StarMultiDefault')}
                  </FormHelperText>
                </FormControl>
              )}
            />

          </Stack>

          <Divider sx={{ my: 3 }} />

          <Step n={3}>{t('Customization_Behaviour')}</Step>
          <Stack spacing={1.5}>
            {/* First, because it gates the other three: a hidden field is never required
                and never reaches a report. */}
            <ToggleRow
              label={t('Customization_Visible')}
              description={t('Customization_VisibleHelp')}
              checked={draft.visible !== false}
              onChange={(checked) => patch({ visible: checked })}
            />
            {/* Disabled rather than hidden on a type that always holds an answer: it could
                never block the form, and a switch that is there but off says why. */}
            <ToggleRow
              label={t('Customization_Required')}
              description={alwaysAnswered ? t('Customization_RequiredNotApplicable') : t('Customization_RequiredHelp')}
              checked={draft.required}
              disabled={alwaysAnswered}
              onChange={(checked) => patch({ required: checked })}
            />
            <ToggleRow
              label={t('Customization_ReadOnly')}
              description={t('Customization_ReadOnlyHelp')}
              checked={draft.readOnly}
              onChange={(checked) => patch({ readOnly: checked })}
            />
            <ToggleRow
              label={t('Customization_ShowOnReports')}
              description={t('Customization_ShowOnReportsHelp')}
              checked={draft.showOnReports}
              onChange={(checked) => patch({ showOnReports: checked })}
            />
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Step n={4}>{t('Customization_Rules')}</Step>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Customization_RulesHelpStatus')}
          </Typography>
          <RuleEditor
            rules={draft.rules ?? []}
            onChange={(rules) => patch({ rules: rules.length ? rules : undefined })}
            candidates={candidates}
            selfId={draft.id}
            targets={alwaysAnswered ? ruleTargets.filter((target) => target !== 'required') : ruleTargets}
            baseVisible={draft.visible !== false}
            onBaseVisibleChange={(visible) => patch({ visible })}
            disabled={saving}
          />
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" spacing={1} sx={drawerFooterStyles}>
          {/* The caption takes the free space, which is what pushes the buttons right. */}
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {t('Customization_FieldSavesTo', [typeName])}
          </Typography>
          <Button color="inherit" size="small" onClick={onClose} sx={buttonStyles} disabled={saving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={!canSave}
            onClick={() => onSave(draft, target)}
            sx={buttonStyles}
          >
            {t('Common_Save')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FieldDrawer;
